import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { chatSessionDetailQueryOptions } from "@/hooks/use-chat-feedbacks";
import type {
  ChatFeedbackItem,
  ChatFeedbackRating,
  ChatSessionMessage,
} from "@/types/chat-feedback";
import { REASON_LABEL } from "@/types/chat-feedback";
import { RatingBadge } from "./rating-badge";

interface Props {
  feedback: ChatFeedbackItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function MessageBubble({
  msg,
  isHighlighted,
  innerRef,
}: {
  msg: ChatSessionMessage;
  isHighlighted: boolean;
  innerRef?: React.Ref<HTMLDivElement>;
}) {
  const isAssistant = msg.role === "assistant";
  return (
    <div
      ref={innerRef}
      className={[
        "rounded-md p-3 text-sm whitespace-pre-wrap",
        isAssistant ? "bg-muted/50" : "bg-background border",
        isHighlighted
          ? "border-l-4 border-red-500 pl-3 bg-red-500/5"
          : "",
      ].join(" ")}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground">
          {msg.role === "assistant"
            ? "AI"
            : msg.role === "user"
              ? "유저"
              : "시스템"}
        </span>
        <span className="text-xs text-muted-foreground">
          {new Date(msg.createdAt).toLocaleString("ko-KR")}
        </span>
      </div>
      <p>{msg.content}</p>
      {msg.feedbackRating && (
        <div className="mt-2 flex flex-wrap gap-1 pt-2 border-t border-border/60">
          <RatingBadge rating={msg.feedbackRating as ChatFeedbackRating} />
          {msg.feedbackReasons?.map((r) => (
            <Badge key={r} variant="secondary">
              {REASON_LABEL[r as keyof typeof REASON_LABEL] ?? r}
            </Badge>
          ))}
          {msg.feedbackDetails && (
            <p className="basis-full text-xs italic text-muted-foreground mt-1">
              “{msg.feedbackDetails}”
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function FeedbackDetailDrawer({ feedback, open, onOpenChange }: Props) {
  const highlightedRef = useRef<HTMLDivElement | null>(null);

  const query = useQuery(
    chatSessionDetailQueryOptions(
      feedback?.sessionId ?? null,
      feedback?.messageId ?? null,
    ),
  );

  useEffect(() => {
    if (open && highlightedRef.current) {
      const timer = setTimeout(() => {
        highlightedRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open, query.data]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full md:max-w-[min(800px,66vw)] overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>세션 문맥</SheetTitle>
          <SheetDescription>
            피드백이 달린 메시지와 주변 대화를 확인합니다. (최대 ±20개,
            soft-deleted 제외)
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {query.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          )}

          {query.isError && (
            <div className="rounded border p-4 text-sm text-destructive">
              세션을 불러오지 못했습니다.
            </div>
          )}

          {query.data && (
            <>
              <div className="space-y-1 rounded-md border bg-muted/30 p-3 text-sm">
                <div>
                  <span className="text-muted-foreground">세션:</span>{" "}
                  <span className="font-medium">
                    {query.data.title || "(제목 없음)"}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Session ID: {query.data.sessionId}
                </div>
                {query.data.workspaceId && (
                  <div className="text-xs text-muted-foreground">
                    Workspace: {query.data.workspaceId}
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  메시지 {query.data.messages.length}개
                  {query.data.truncated && " · (일부만 표시됨)"}
                </div>
              </div>

              <div className="space-y-3">
                {query.data.messages.map((m) => {
                  const isHighlighted = m.messageId === feedback?.messageId;
                  return (
                    <MessageBubble
                      key={m.messageId}
                      msg={m}
                      isHighlighted={isHighlighted}
                      innerRef={isHighlighted ? highlightedRef : undefined}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
